// RecipeResolver — D10 inheritance: walks baseRecipeID chains root-ward, applies outward (layer
// concat root-first, disabledLayerIDs, paramOverrides then layerArgOverrides, derived wins), and
// produces the structural hash (program-cache key). Params-only changes hash identically.
import {
	MacroModConfig, MaterialLayer, MaterialRecipe, ParamSchema,
	isModRef, isParamRef,
	type LayerArg, type LayerContribution, type ParamRef, type ParamScalar, type ParamValue,
} from "./@MaterialTypes.js";

export const RESOLVER_DEPTH_CAP = 8;

export class RecipeResolutionError extends Error {
	constructor(message: string) { super(message); this.name = "RecipeResolutionError"; }
}

export interface ResolvedLayer {
	id: string;
	primitive: MaterialLayer["primitive"];
	args: {[argKey: string]: LayerArg};
	blend: MaterialLayer["blend"];
	blendArgs: {[argKey: string]: LayerArg};
	contributes: LayerContribution;
	/** Set11 Phase 2 — the layer's own roughness arg (see MaterialLayer.roughnessOut). */
	roughnessOut?: LayerArg;
}

export interface ResolvedParam {
	key: string;
	label: string;
	kind: ParamSchema["kind"];
	min: number;
	max: number;
	default: ParamValue;
	regionModulated: boolean;
}

export interface ResolvedRecipe {
	id: string;
	name: string;
	roughness: number;
	/** Per-recipe multiplier on the global specular lobe (0 = opt out). See MaterialRecipe.specStrength. */
	specStrength: number;
	/** Use a straight-up geo normal (detail normal only). See MaterialRecipe.flatGeoNormal. */
	flatGeoNormal: boolean;
	/** Per-recipe light altitude in degrees (null = use the global rig). See MaterialRecipe.lightAltitudeOverride. */
	lightAltitudeOverride: number | null;
	/** Option D — the per-recipe macro modulator (null = none; g_macroMod stays 0 and every ModRef collapses to its base). See MaterialRecipe.macroMod. */
	macroMod: MacroModConfig | null;
	layers: ResolvedLayer[];
	params: ResolvedParam[];
	/** Structural hash — the program-cache key. Invariant under params-only value changes. */
	structuralHash: string;
	/** Human-readable canonical form underlying the hash (for debugging hash changes). */
	canonical: string;
	warnings: string[];
	chainIDs: string[];
}

export function ResolveRecipe_ForTests(id: string, allRecipes: MaterialRecipe[]): ResolvedRecipe {
	// Walk the base chain root-ward, tracking cycles + depth.
	const chain: MaterialRecipe[] = [];
	const visited = new Set<string>();
	let cur = allRecipes.find(r=>r.id == id) ?? null;
	if (cur == null) throw new RecipeResolutionError(`recipe "${id}" not found in catalogue`);
	while (cur != null) {
		if (visited.has(cur.id)) throw new RecipeResolutionError(`cycle detected: ${[...visited, cur.id].join(" -> ")}`);
		visited.add(cur.id);
		if (visited.size > RESOLVER_DEPTH_CAP) throw new RecipeResolutionError(`depth cap (${RESOLVER_DEPTH_CAP}) exceeded: ${[...visited].join(" -> ")}`);
		chain.unshift(cur);
		if (cur.baseRecipeID == null) break;
		cur = allRecipes.find(r=>r.id == cur!.baseRecipeID) ?? null;
	}

	return resolveRecipeFromChain(chain);
}

export function resolveRecipeFromChain(chain: MaterialRecipe[]): ResolvedRecipe {
	const id = chain[chain.length - 1].id;

	// Apply root-first: layers concat, then appends of descendants (chain is root → leaf).
	const layers: ResolvedLayer[] = [];
	const params: ResolvedParam[] = [];
	const warnings: string[] = [];
	for (const recipe of chain) {
		for (const l of recipe.layers) {
			//layers.filter(a=>a.id == l.id).forEach(a=>layers.Remove(a)); // if earlier layer with same id, remove it
			layers.push({id: l.id, primitive: l.primitive, args: {...l.args}, blend: l.blend, blendArgs: {...l.blendArgs}, contributes: l.contributes, roughnessOut: l.roughnessOut});
		}
		for (const p of recipe.params) {
			//params.filter(a=>a.key == p.key).forEach(a=>params.Remove(a)); // if earlier param with same key, remove it
			params.push({key: p.key, label: p.label, kind: p.kind, min: p.min, max: p.max, default: p.default, regionModulated: p.regionModulated});
		}
	}

	// disabledLayerIDs — collect across the whole chain (base drift: unknown ids are warnings, not errors).
	for (const recipe of chain) {
		for (const lid of recipe.disabledLayerIDs) {
			const idx = layers.findIndex(l=>l.id == lid);
			if (idx >= 0) layers.splice(idx, 1);
			else warnings.push(`recipe "${recipe.id}" disables layer "${lid}" which is not in the resolved base`);
		}
	}

	// paramOverrides — derived wins (apply in chain order, later recipes override earlier).
	for (const recipe of chain) {
		for (const [key, value] of Object.entries(recipe.paramOverrides)) {
			const p = params.find(p=>p.key == key);
			if (p) p.default = value;
			else warnings.push(`recipe "${recipe.id}" overrides param "${key}" which is not in the resolved params`);
		}
	}

	// layerArgOverrides ("layerId:argKey") — derived wins.
	for (const recipe of chain) {
		for (const [addr, value] of Object.entries(recipe.layerArgOverrides)) {
			const sep = addr.indexOf(":");
			const lid = sep >= 0 ? addr.slice(0, sep) : addr;
			const argKey = sep >= 0 ? addr.slice(sep + 1) : "";
			const layer = layers.find(l=>l.id == lid);
			if (layer == null) {
				warnings.push(`recipe "${recipe.id}" overrides layer arg "${addr}" but layer "${lid}" is not in the resolved base`);
				continue;
			}
			layer.args[argKey] = value;
		}
	}

	const leaf = chain[chain.length - 1];
	// Leaf fields use null-as-inherit: walk the chain root-ward, taking the LAST explicit value
	// (0/false ARE explicit).  Fallbacks: roughness 0.85, specStrength 1, flatGeoNormal false.
	const chainValue = <T,>(get: (r: MaterialRecipe)=>T|null): T|null=>{
		for (let i = chain.length - 1; i >= 0; i--) {
			const v = get(chain[i]);
			if (v != null) return v;
		}
		return null;
	};
	// roughness participates in the hash: it is baked as a GLSL literal into the eval, so two
	// recipes differing only in it are DIFFERENT programs and must not share a cache entry.
	const roughness = chainValue(r=>r.roughness) ?? 0.85;
	// specStrength participates in the hash: it is baked as a GLSL literal into the lit call, so two
	// recipes differing only in it are DIFFERENT programs and must not share a cache entry.
	const specStrength = chainValue(r=>r.specStrength) ?? 1;
	// Also hashed: it swaps the geo-normal EXPRESSION in the emitted lit call, so it is structural.
	const flatGeoNormal = chainValue(r=>r.flatGeoNormal) ?? false;
	// Also baked as a GLSL literal (the lit call's 6th arg), so likewise structural.
	const lightAltitudeOverride = chainValue(r=>r.lightAltitudeOverride) ?? null;
	// Option D — also structural: its values are baked as GLSL literals into the macroModVal call at the top of
	// eval_N, so two recipes differing only in it are DIFFERENT programs.
	const macroMod = chainValue(r=>r.macroMod) ?? null;
	const {canonical, hash} = structuralHash(roughness, specStrength, flatGeoNormal, lightAltitudeOverride, macroMod, layers, params);
	const resolved: ResolvedRecipe = {
		id, name: leaf.name, roughness, specStrength, flatGeoNormal, lightAltitudeOverride, macroMod,
		layers, params, structuralHash: hash, canonical, warnings,
		chainIDs: chain.map(c=>c.id),
	};
	return resolved;
}

/** Structural hash of the ACTIVE SET: recipes sorted by id (order-independent), versioned
 *  delimiter. D1 program-cache key at set level; identical to ShaderAssembler.materialSetHash
 *  (kept here so Phase 6's warm-up can dedupe WITHOUT importing the assembler). */
export function recipeSetHash(resolved: ResolvedRecipe[]): string {
	const sorted = [...resolved].map(r=>r.structuralHash).sort().join("~");
	return `set:v1:${sorted}`;
}

/** Structural hash (cache key) + canonical form.  Recipe ID DELIBERATELY excluded — identical
 *  resolved structures → identical GLSL → one cached program (the D10 free-variant property).
 *  Params contribute keys+kinds ONLY (never values); literals hash by value; refs by param key. */
function structuralHash(
	roughness: number,
	specStrength: number,
	flatGeoNormal: boolean,
	lightAltitudeOverride: number | null,
	macroMod: MacroModConfig | null,
	layers: ResolvedLayer[], params: ResolvedParam[],
): {canonical: string; hash: string} {
	const parts: string[] = [];
	parts.push(`R:${roughness}`);
	parts.push(`S:${specStrength}`);
	parts.push(`F:${flatGeoNormal ? 1 : 0}`);
	if (lightAltitudeOverride != null) parts.push(`A:${lightAltitudeOverride}`);
	// Emitted ONLY when present, so a recipe without a modulator hashes exactly as before Option D (its D1 cache key stays valid).
	// A field that is a ParamRef hashes by KEY only (a params-only value change is a FREE VARIANT — one shared program, D10);
	// a literal hashes by VALUE (it is baked into the GLSL, so two different literals are different programs).
	if (macroMod != null) {
		const mf = (v: ParamScalar | ParamRef): string => isParamRef(v) ? `ref:${v.ref}` : `lit:${JSON.stringify(v)}`;
		parts.push(`M:${mf(macroMod.wavelength)}:${mf(macroMod.octaves)}:${mf(macroMod.lacunarity)}:${mf(macroMod.persistence)}:${mf(macroMod.contrast)}:${mf(macroMod.seedOffset)}`);
	}
	for (const p of params) parts.push(`P:${p.key}:${p.kind}:${p.regionModulated ? 1 : 0}`);
	// A ModRef is STRUCTURAL (its amount/op bake as GLSL literals into the arg expression), so it hashes distinctly from the bare base it wraps.
	const argPart = (k: string, v: LayerArg): string=>{
		if (isModRef(v)) {
			const inner = isParamRef(v.base) ? `ref:${v.base.ref}` : `lit:${JSON.stringify(v.base)}`;
			// The amount may be a ParamRef (a live modulation-strength slider); a ref and a literal emit DIFFERENT GLSL → hash differently.
			const amt = isParamRef(v.amount) ? `ref:${v.amount.ref}` : `lit:${JSON.stringify(v.amount)}`;
			return `${k}=mod:${inner}:${amt}:${v.op ?? "mul"}`;
		}
		return isParamRef(v) ? `${k}=ref:${v.ref}` : `${k}=lit:${JSON.stringify(v)}`;
	};
	for (const l of layers) {
		const argParts = Object.keys(l.args).sort().map(k=>argPart(k, l.args[k]));
		const baParts = Object.keys(l.blendArgs).sort().map(k=>argPart(k, l.blendArgs[k]));
		const ro = l.roughnessOut == null ? "" : (isParamRef(l.roughnessOut) ? `|ro=ref:${l.roughnessOut.ref}` : `|ro=lit:${JSON.stringify(l.roughnessOut)}`);
		parts.push(`L:${l.id}:${l.primitive}:{${argParts.join(",")}}:${l.blend}:{${baParts.join(",")}}:${l.contributes}${ro}`);
	}
	const canonical = parts.join("|");
	return {canonical, hash: fnv1a(canonical)};
}

/** FNV-1a 32-bit, hex-encoded — compact deterministic hash of the canonical form. */
function fnv1a(s: string): string {
	let h = 0x811c9dc5;
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i);
		h = (h * 0x01000193) >>> 0;
	}
	return h.toString(16).padStart(8, "0");
}